<script setup lang="ts">
// レイヤースタイルダイアログ。左に効果の一覧 (チェック)、右に選択中の効果のパラメータ。
// 変更はトランザクションでまとめ、OK で確定、キャンセルで元に戻す。
import { computed, ref } from 'vue'
import { useDocumentStore } from '@/stores/documentStore'
import { useModal } from '@/composables/useModalStack'
import { useI18n, type I18nKey } from '@/i18n'
import { BLEND_MODES, type LayerStyle, type BlendMode, type BlendIf } from '@/types/document'
import { defaultStyle, STYLE_KEYS } from '@/model/defaults'
import { blendModeLabel } from '@/components/common/blendModeLabels'
import SliderField from '@/components/common/SliderField.vue'
import GradientEditor from '@/components/common/GradientEditor.vue'

const emit = defineEmits<{ close: [] }>()
useModal('layer-style')
const { t } = useI18n()
const docStore = useDocumentStore()
const layer = computed(() => docStore.activeLayer)
const layerId = layer.value?.id ?? ''
const styles = computed<LayerStyle>(() => layer.value?.styles ?? {})
const selected = ref<keyof LayerStyle | 'blendIf'>((STYLE_KEYS.find(k => styles.value[k]?.enabled) as keyof LayerStyle) ?? 'dropShadow')
const blendIf = computed(() => layer.value?.blendIf ?? null)
const DEFAULT_BLEND_IF: BlendIf = { channel: 'gray', thisLayer: [0, 0, 255, 255], underlying: [0, 0, 255, 255] }
function toggleBlendIf(on: boolean) {
  docStore.setLayerProps(layerId, { blendIf: on ? { ...DEFAULT_BLEND_IF, thisLayer: [...DEFAULT_BLEND_IF.thisLayer], underlying: [...DEFAULT_BLEND_IF.underlying] } : null })
  selected.value = 'blendIf'
}
function setBlendIf(side: 'thisLayer' | 'underlying', i: number, v: number) {
  const cur = blendIf.value ?? DEFAULT_BLEND_IF
  const q = [...cur[side]] as [number, number, number, number]
  q[i] = Math.max(0, Math.min(255, Math.round(v)))
  // 昇順を保つ
  for (let k = 1; k < 4; k++) if (q[k]! < q[k - 1]!) q[k] = q[k - 1]!
  for (let k = 2; k >= 0; k--) if (q[k]! > q[k + 1]!) q[k] = q[k + 1]!
  docStore.setLayerProps(layerId, { blendIf: { ...cur, [side]: q } })
}
function setBlendIfChannel(ch: BlendIf['channel']) {
  const cur = blendIf.value ?? DEFAULT_BLEND_IF
  docStore.setLayerProps(layerId, { blendIf: { ...cur, channel: ch } })
}

docStore.beginTransaction('history.layerStyle')
function write(next: LayerStyle) {
  docStore.setStyles(layerId, next)
}
function toggle(key: keyof LayerStyle, on: boolean) {
  const next = { ...styles.value }
  if (on) next[key] = { ...(next[key] ?? defaultStyle(key)), enabled: true } as never
  else if (next[key]) next[key] = { ...next[key]!, enabled: false } as never
  write(next)
  selected.value = key
}
function patch<K extends keyof LayerStyle>(key: K | 'blendIf', p: Partial<NonNullable<LayerStyle[K]>>) {
  if (key === 'blendIf') return
  const cur = styles.value[key] ?? defaultStyle(key)
  write({ ...styles.value, [key]: { ...cur, ...p } })
}
const cur = computed(() => (selected.value === 'blendIf' ? {} : (styles.value[selected.value] ?? defaultStyle(selected.value))) as unknown as Record<string, unknown>)
function ok() {
  docStore.commitTransaction()
  emit('close')
}
function cancel() {
  docStore.rollbackTransaction()
  emit('close')
}
function clearAll() {
  write({})
}
const blends = BLEND_MODES as readonly BlendMode[]
</script>

<template>
  <div class="modal-backdrop" @pointerdown.self="cancel">
    <div class="modal style-modal" @keydown.esc="cancel">
      <h2>{{ t('dialog.style.title') }} — {{ layer?.name }}</h2>
      <div class="body">
        <div class="list">
          <label v-for="k in STYLE_KEYS" :key="k" class="item" :class="{ sel: selected === k }" @click="selected = k">
            <input type="checkbox" :checked="!!styles[k]?.enabled" @change="toggle(k, ($event.target as HTMLInputElement).checked)" @click.stop />
            <span>{{ t(`fx.${k}` as I18nKey) }}</span>
          </label>
          <label class="item" :class="{ sel: selected === 'blendIf' }" @click="selected = 'blendIf'">
            <input type="checkbox" :checked="!!blendIf" @change="toggleBlendIf(($event.target as HTMLInputElement).checked)" @click.stop />
            <span>{{ t('dialog.style.blendIf') }}</span>
          </label>
          <button class="ghost" @click="clearAll">{{ t('dialog.style.clear') }}</button>
        </div>
        <div class="params">
          <h3>{{ selected === 'blendIf' ? t('dialog.style.blendIf') : t(`fx.${selected}` as I18nKey) }}</h3>
          <p v-if="selected !== 'blendIf' && !styles[selected]?.enabled" class="muted">☐ → ☑ で有効化</p>
          <template v-if="selected === 'blendIf'">
            <p v-if="!blendIf" class="muted">☐ → ☑ で有効化</p>
            <template v-else>
              <label class="row"><span class="lbl">{{ t('dialog.style.blendIfChannel') }}</span>
                <select :value="blendIf.channel" @change="setBlendIfChannel(($event.target as HTMLSelectElement).value as BlendIf['channel'])">
                  <option value="gray">{{ t('dialog.style.gray') }}</option><option value="r">R</option><option value="g">G</option><option value="b">B</option>
                </select>
              </label>
              <div v-for="side in (['thisLayer', 'underlying'] as const)" :key="side" class="bi">
                <span class="lbl">{{ t(side === 'thisLayer' ? 'dialog.style.thisLayer' : 'dialog.style.underlying') }}</span>
                <span class="row">
                  <input v-for="i in 4" :key="i" class="num mono" type="number" min="0" max="255" :value="blendIf[side][i - 1]" @change="setBlendIf(side, i - 1, Number(($event.target as HTMLInputElement).value))" />
                </span>
                <div class="bar" :style="{ background: `linear-gradient(90deg, #000 ${blendIf[side][0] / 2.55}%, #fff ${blendIf[side][1] / 2.55}%, #fff ${blendIf[side][2] / 2.55}%, #000 ${blendIf[side][3] / 2.55}%)` }" />
              </div>
              <p class="muted small">{{ t('dialog.style.blendIfHint') }}</p>
            </template>
          </template>
          <template v-if="selected === 'patternOverlay'">
            <label class="row"><span class="lbl">{{ t('dialog.style.blend') }}</span><select :value="cur.blendMode" @change="patch('patternOverlay', { blendMode: ($event.target as HTMLSelectElement).value as BlendMode })"><option v-for="m in blends" :key="m" :value="m">{{ blendModeLabel(m) }}</option></select></label>
            <SliderField :label="t('dialog.style.opacity')" :value="Number(cur.opacity) * 100" :min="0" :max="100" unit="%" @input="v => patch('patternOverlay', { opacity: v / 100 })" @change="v => patch('patternOverlay', { opacity: v / 100 })" />
            <label class="row"><span class="lbl">{{ t('pattern.title') }}</span>
              <select :value="cur.patternId" @change="patch('patternOverlay', { patternId: ($event.target as HTMLSelectElement).value })">
                <option value="">—</option>
                <option v-for="p in docStore.doc?.patterns ?? []" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </label>
            <SliderField :label="t('fill.scale')" :value="Number(cur.scale ?? 1)" :min="0.05" :max="10" :step="0.01" @input="v => patch('patternOverlay', { scale: v })" @change="v => patch('patternOverlay', { scale: v })" />
            <p v-if="!(docStore.doc?.patterns ?? []).length" class="muted small">{{ t('pattern.none') }}</p>
          </template>
          <template v-if="selected === 'dropShadow' || selected === 'innerShadow'">
            <label class="row"><span class="lbl">{{ t('dialog.style.blend') }}</span><select :value="cur.blendMode" @change="patch(selected, { blendMode: ($event.target as HTMLSelectElement).value as BlendMode } as never)"><option v-for="m in blends" :key="m" :value="m">{{ blendModeLabel(m) }}</option></select></label>
            <label class="row"><span class="lbl">{{ t('dialog.style.color') }}</span><input type="color" :value="cur.hex" @change="patch(selected, { hex: ($event.target as HTMLInputElement).value } as never)" /></label>
            <SliderField :label="t('dialog.style.opacity')" :value="Number(cur.opacity) * 100" :min="0" :max="100" unit="%" @input="v => patch(selected, { opacity: v / 100 } as never)" @change="v => patch(selected, { opacity: v / 100 } as never)" />
            <SliderField :label="t('dialog.style.angle')" :value="Number(cur.angle)" :min="-180" :max="180" unit="°" @input="v => patch(selected, { angle: v } as never)" @change="v => patch(selected, { angle: v } as never)" />
            <SliderField :label="t('dialog.style.distance')" :value="Number(cur.distance)" :min="0" :max="300" unit="px" @input="v => patch(selected, { distance: v } as never)" @change="v => patch(selected, { distance: v } as never)" />
            <SliderField :label="selected === 'dropShadow' ? t('dialog.style.spread') : t('dialog.style.choke')" :value="Number(cur.spread)" :min="0" :max="100" unit="%" @input="v => patch(selected, { spread: v } as never)" @change="v => patch(selected, { spread: v } as never)" />
            <SliderField :label="t('dialog.style.size')" :value="Number(cur.size)" :min="0" :max="250" unit="px" @input="v => patch(selected, { size: v } as never)" @change="v => patch(selected, { size: v } as never)" />
          </template>
          <template v-else-if="selected === 'outerGlow' || selected === 'innerGlow'">
            <label class="row"><span class="lbl">{{ t('dialog.style.blend') }}</span><select :value="cur.blendMode" @change="patch(selected, { blendMode: ($event.target as HTMLSelectElement).value as BlendMode } as never)"><option v-for="m in blends" :key="m" :value="m">{{ blendModeLabel(m) }}</option></select></label>
            <label class="row"><span class="lbl">{{ t('dialog.style.color') }}</span><input type="color" :value="cur.hex" @change="patch(selected, { hex: ($event.target as HTMLInputElement).value } as never)" /></label>
            <SliderField :label="t('dialog.style.opacity')" :value="Number(cur.opacity) * 100" :min="0" :max="100" unit="%" @input="v => patch(selected, { opacity: v / 100 } as never)" @change="v => patch(selected, { opacity: v / 100 } as never)" />
            <SliderField :label="selected === 'outerGlow' ? t('dialog.style.spread') : t('dialog.style.choke')" :value="Number(cur.spread)" :min="0" :max="100" unit="%" @input="v => patch(selected, { spread: v } as never)" @change="v => patch(selected, { spread: v } as never)" />
            <SliderField :label="t('dialog.style.size')" :value="Number(cur.size)" :min="0" :max="250" unit="px" @input="v => patch(selected, { size: v } as never)" @change="v => patch(selected, { size: v } as never)" />
            <label v-if="selected === 'innerGlow'" class="row"><span class="lbl">{{ t('dialog.style.source') }}</span><select :value="cur.source ?? 'edge'" @change="patch('innerGlow', { source: ($event.target as HTMLSelectElement).value as 'edge' })"><option value="edge">{{ t('dialog.style.edge') }}</option><option value="center">{{ t('dialog.style.centerSource') }}</option></select></label>
          </template>
          <template v-else-if="selected === 'bevel'">
            <label class="row"><span class="lbl">{{ t('dialog.style.styleKind') }}</span><select :value="cur.style" @change="patch('bevel', { style: ($event.target as HTMLSelectElement).value as 'inner' })"><option value="inner">{{ t('dialog.style.inner') }}</option><option value="outer">{{ t('dialog.style.outer') }}</option><option value="emboss">{{ t('dialog.style.emboss') }}</option></select></label>
            <SliderField :label="t('dialog.style.depth')" :value="Number(cur.depth)" :min="1" :max="1000" unit="%" @input="v => patch('bevel', { depth: v })" @change="v => patch('bevel', { depth: v })" />
            <SliderField :label="t('dialog.style.size')" :value="Number(cur.size)" :min="0" :max="250" unit="px" @input="v => patch('bevel', { size: v })" @change="v => patch('bevel', { size: v })" />
            <SliderField :label="t('dialog.style.soften')" :value="Number(cur.soften)" :min="0" :max="16" unit="px" @input="v => patch('bevel', { soften: v })" @change="v => patch('bevel', { soften: v })" />
            <SliderField :label="t('dialog.style.angle')" :value="Number(cur.angle)" :min="-180" :max="180" unit="°" @input="v => patch('bevel', { angle: v })" @change="v => patch('bevel', { angle: v })" />
            <SliderField :label="t('dialog.style.altitude')" :value="Number(cur.altitude)" :min="0" :max="90" unit="°" @input="v => patch('bevel', { altitude: v })" @change="v => patch('bevel', { altitude: v })" />
            <label class="row"><span class="lbl">{{ t('dialog.style.highlight') }}</span><input type="color" :value="cur.highlightHex" @change="patch('bevel', { highlightHex: ($event.target as HTMLInputElement).value })" /></label>
            <SliderField :label="t('dialog.style.opacity')" :value="Number(cur.highlightOpacity) * 100" :min="0" :max="100" unit="%" @input="v => patch('bevel', { highlightOpacity: v / 100 })" @change="v => patch('bevel', { highlightOpacity: v / 100 })" />
            <label class="row"><span class="lbl">{{ t('dialog.style.shadow') }}</span><input type="color" :value="cur.shadowHex" @change="patch('bevel', { shadowHex: ($event.target as HTMLInputElement).value })" /></label>
            <SliderField :label="t('dialog.style.opacity')" :value="Number(cur.shadowOpacity) * 100" :min="0" :max="100" unit="%" @input="v => patch('bevel', { shadowOpacity: v / 100 })" @change="v => patch('bevel', { shadowOpacity: v / 100 })" />
          </template>
          <template v-else-if="selected === 'satin'">
            <label class="row"><span class="lbl">{{ t('dialog.style.blend') }}</span><select :value="cur.blendMode" @change="patch('satin', { blendMode: ($event.target as HTMLSelectElement).value as BlendMode })"><option v-for="m in blends" :key="m" :value="m">{{ blendModeLabel(m) }}</option></select></label>
            <label class="row"><span class="lbl">{{ t('dialog.style.color') }}</span><input type="color" :value="cur.hex" @change="patch('satin', { hex: ($event.target as HTMLInputElement).value })" /></label>
            <SliderField :label="t('dialog.style.opacity')" :value="Number(cur.opacity) * 100" :min="0" :max="100" unit="%" @input="v => patch('satin', { opacity: v / 100 })" @change="v => patch('satin', { opacity: v / 100 })" />
            <SliderField :label="t('dialog.style.angle')" :value="Number(cur.angle)" :min="-180" :max="180" unit="°" @input="v => patch('satin', { angle: v })" @change="v => patch('satin', { angle: v })" />
            <SliderField :label="t('dialog.style.distance')" :value="Number(cur.distance)" :min="0" :max="250" unit="px" @input="v => patch('satin', { distance: v })" @change="v => patch('satin', { distance: v })" />
            <SliderField :label="t('dialog.style.size')" :value="Number(cur.size)" :min="0" :max="250" unit="px" @input="v => patch('satin', { size: v })" @change="v => patch('satin', { size: v })" />
            <label class="row"><input type="checkbox" :checked="!!cur.invert" @change="patch('satin', { invert: ($event.target as HTMLInputElement).checked })" /><span>{{ t('dialog.style.invert') }}</span></label>
          </template>
          <template v-else-if="selected === 'colorOverlay' || selected === 'gradientOverlay'">
            <label class="row"><span class="lbl">{{ t('dialog.style.blend') }}</span><select :value="cur.blendMode" @change="patch(selected, { blendMode: ($event.target as HTMLSelectElement).value as BlendMode } as never)"><option v-for="m in blends" :key="m" :value="m">{{ blendModeLabel(m) }}</option></select></label>
            <SliderField :label="t('dialog.style.opacity')" :value="Number(cur.opacity) * 100" :min="0" :max="100" unit="%" @input="v => patch(selected, { opacity: v / 100 } as never)" @change="v => patch(selected, { opacity: v / 100 } as never)" />
            <label v-if="selected === 'colorOverlay'" class="row"><span class="lbl">{{ t('dialog.style.color') }}</span><input type="color" :value="cur.hex" @change="patch('colorOverlay', { hex: ($event.target as HTMLInputElement).value })" /></label>
            <template v-else>
              <GradientEditor :stops="(cur.stops as { pos: number; hex: string }[]) ?? []" @input="st => patch('gradientOverlay', { stops: st.map(s => ({ pos: s.pos, hex: s.hex })) })" @change="st => patch('gradientOverlay', { stops: st.map(s => ({ pos: s.pos, hex: s.hex })) })" />
              <SliderField :label="t('dialog.style.angle')" :value="Number(cur.angle ?? 90)" :min="-180" :max="180" unit="°" @input="v => patch('gradientOverlay', { angle: v })" @change="v => patch('gradientOverlay', { angle: v })" />
              <label class="row"><span class="lbl">{{ t('dialog.style.styleKind') }}</span><select :value="cur.style ?? 'linear'" @change="patch('gradientOverlay', { style: ($event.target as HTMLSelectElement).value as 'linear' })"><option value="linear">{{ t('options.gradient.linear') }}</option><option value="radial">{{ t('options.gradient.radial') }}</option></select></label>
            </template>
          </template>
          <template v-else-if="selected === 'stroke'">
            <SliderField :label="t('dialog.style.size')" :value="Number(cur.size)" :min="0" :max="250" unit="px" @input="v => patch('stroke', { size: v })" @change="v => patch('stroke', { size: v })" />
            <label class="row"><span class="lbl">{{ t('dialog.style.position') }}</span><select :value="cur.position" @change="patch('stroke', { position: ($event.target as HTMLSelectElement).value as 'outside' })"><option value="outside">{{ t('dialog.style.outside') }}</option><option value="inside">{{ t('dialog.style.inside') }}</option><option value="center">{{ t('dialog.style.center') }}</option></select></label>
            <label class="row"><span class="lbl">{{ t('dialog.style.blend') }}</span><select :value="cur.blendMode" @change="patch('stroke', { blendMode: ($event.target as HTMLSelectElement).value as BlendMode })"><option v-for="m in blends" :key="m" :value="m">{{ blendModeLabel(m) }}</option></select></label>
            <SliderField :label="t('dialog.style.opacity')" :value="Number(cur.opacity) * 100" :min="0" :max="100" unit="%" @input="v => patch('stroke', { opacity: v / 100 })" @change="v => patch('stroke', { opacity: v / 100 })" />
            <label class="row"><span class="lbl">{{ t('dialog.style.color') }}</span><input type="color" :value="cur.hex" @change="patch('stroke', { hex: ($event.target as HTMLInputElement).value })" /></label>
          </template>
        </div>
      </div>
      <div class="actions">
        <button @click="cancel">{{ t('dialog.cancel') }}</button>
        <button class="primary" @click="ok">{{ t('dialog.ok') }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.style-modal {
  width: 720px;
  max-width: 96vw;
}
.body {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 14px;
  min-height: 320px;
}
.list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  border-right: 1px solid var(--line);
  padding-right: 10px;
}
.item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 6px;
  border-radius: var(--radius-sm);
  cursor: default;
}
.item.sel {
  background: var(--accent-dim);
}
.params {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.params h3 {
  margin: 0 0 4px;
  font-size: 13px;
}
.lbl {
  width: 78px;
  color: var(--fg-2);
  font-size: 11px;
}
.bi {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 6px 0;
}
.bi .num {
  width: 52px;
}
.bi .bar {
  height: 10px;
  border: 1px solid var(--line-strong);
  border-radius: 2px;
}
.small {
  font-size: 10px;
}
</style>
